import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import api from '../../utils/api';

const shellClass =
  'flex flex-col items-center justify-center py-10 px-4 sm:px-6 lg:px-8 w-full max-w-7xl mx-auto flex-1';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);

  const seo = (
    <Helmet>
      <title>Reset Password | Receevit®</title>
      <meta name="description" content="Reset your Receevit® password securely using your reset link. Enter and confirm your new password to regain access." />
      <link rel="canonical" href="https://www.receevit.com/reset-password-confirm" />
      <meta property="og:title" content="Reset Password | Receevit®" />
      <meta property="og:description" content="Reset your Receevit® password securely using your reset link. Enter and confirm your new password to regain access." />
      <meta property="og:url" content="https://www.receevit.com/reset-password-confirm" />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="https://www.receevit.com/og-image.png" />
    </Helmet>
  );

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      setError('No reset token provided');
      return;
    }
    setTokenValid(true);
  }, [token]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (error) setError(null);
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return {
      isValid: password.length >= minLength && hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar,
      errors: {
        minLength: password.length < minLength,
        noUpperCase: !hasUpperCase,
        noLowerCase: !hasLowerCase,
        noNumbers: !hasNumbers,
        noSpecialChar: !hasSpecialChar
      }
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordValidation = validatePassword(formData.newPassword);
    if (!passwordValidation.isValid) {
      setError('Password does not meet security requirements');
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post('/api/auth/confirm-password-reset', {
        token,
        newPassword: formData.newPassword
      });

      if (response.data.success || response.data.message === 'Password reset successfully') {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(response.data.message || 'Password reset failed');
      }
    } catch (error) {
      console.error('Password reset error:', error);

      if (error.response?.status === 404) {
        setError('Invalid or expired password reset token');
      } else if (error.response?.status === 400) {
        setError(error.response.data.error || 'Password reset failed');
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (tokenValid === false) {
    return (
      <div className={shellClass}>
        {seo}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="w-full max-w-md space-y-6"
        >
          <div className="text-center">
            <AlertCircle className="mx-auto h-14 w-14 text-red-400 mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Invalid or expired link</h1>
            <p className="text-blue-100 text-sm">
              This reset link is invalid or has expired. Request a new one from the forgot password page.
            </p>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.05 }}
            className="bg-white rounded-3xl shadow-2xl p-8 space-y-4"
          >
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">Reset links expire after 1 hour for security.</p>
            </div>
            <Link
              to="/forgot-password"
              className="block w-full text-center bg-[#1F11ED] text-white py-3 px-6 rounded-xl font-semibold hover:bg-blue-700 transition-all duration-200"
            >
              Request new link
            </Link>
            <Link
              to="/login"
              className="block w-full text-center bg-gray-100 text-gray-700 py-3 px-6 rounded-xl font-semibold hover:bg-gray-200 transition-all duration-200"
            >
              Back to login
            </Link>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  if (success) {
    return (
      <div className={shellClass}>
        {seo}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="w-full max-w-md space-y-6"
        >
          <div className="text-center">
            <CheckCircle className="mx-auto h-14 w-14 text-green-400 mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Password updated</h1>
            <p className="text-blue-100 text-sm">You can sign in with your new password.</p>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.05 }}
            className="bg-white rounded-3xl shadow-2xl p-8"
          >
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-green-800 text-center">
                Redirecting to login in a few seconds…
              </p>
            </div>
            <Link
              to="/login"
              className="block w-full text-center bg-[#1F11ED] text-white py-3 px-6 rounded-xl font-semibold hover:bg-blue-700 transition-all duration-200"
            >
              Go to login
            </Link>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className={shellClass}>
      {seo}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="w-full max-w-md space-y-6"
      >
        <div className="text-center">
          <h1 className="text-3xl font-bold text-white mb-2">Set new password</h1>
          <p className="text-blue-100 text-sm">Choose a strong password for your client portal account.</p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.05 }}
          className="bg-white rounded-3xl shadow-2xl p-8"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border border-red-200 rounded-lg p-4"
              >
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400 shrink-0" />
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              </motion.div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  New password
                </label>
                <div className="relative">
                  <input
                    id="newPassword"
                    name="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.newPassword}
                    onChange={handleChange}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1F11ED] focus:border-[#1F11ED] transition-all duration-200"
                    placeholder="Enter new password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm password
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1F11ED] focus:border-[#1F11ED] transition-all duration-200"
                    placeholder="Confirm new password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            </div>

            {formData.newPassword && (
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Password requirements</h4>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li className={formData.newPassword.length >= 8 ? 'text-green-600' : 'text-red-600'}>
                    • At least 8 characters
                  </li>
                  <li className={/[A-Z]/.test(formData.newPassword) ? 'text-green-600' : 'text-red-600'}>
                    • One uppercase letter
                  </li>
                  <li className={/[a-z]/.test(formData.newPassword) ? 'text-green-600' : 'text-red-600'}>
                    • One lowercase letter
                  </li>
                  <li className={/\d/.test(formData.newPassword) ? 'text-green-600' : 'text-red-600'}>
                    • One number
                  </li>
                  <li
                    className={
                      /[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword) ? 'text-green-600' : 'text-red-600'
                    }
                  >
                    • One special character
                  </li>
                </ul>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || !formData.newPassword || !formData.confirmPassword}
              className="w-full bg-[#1F11ED] text-white py-3 px-6 rounded-xl font-semibold hover:bg-blue-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Updating password…' : 'Update password'}
            </button>

            <div className="text-center">
              <Link
                to="/login"
                className="inline-flex items-center text-sm text-[#1F11ED] hover:text-blue-700 font-medium transition-colors duration-200"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to login
              </Link>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ResetPasswordPage;
